import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../api";

function Spinner({ size = 14 }) {
  return (
    <svg className="animate-spin" width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

function Field({ label, value }) {
  return (
    <div className="mb-3">
      <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-sm text-gray-800">{value || "—"}</div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-5 mb-5">
      <h2 className="text-sm font-semibold mb-3" style={{ color: "#154E93" }}>{title}</h2>
      {children}
    </div>
  );
}

function boolLabel(v) {
  if (v === 1 || v === true) return "Yes";
  if (v === 0 || v === false) return "No";
  return "—";
}

export default function ApplicantProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [applicant, setApplicant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  function load() {
    setLoading(true);
    api.recruitment.get(id).then(setApplicant).finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, [id]);

  async function handleApprove() {
    setError("");
    setBusy(true);
    try {
      const result = await api.recruitment.approve(id);
      await load();
      // Nudge toward the new Employee record — that's the whole point of Approve.
      if (result.employee_id) {
        setTimeout(() => navigate(`/admin/employees/${result.employee_id}`), 900);
      }
    } catch (e) {
      setError(e.message || "Could not approve this applicant.");
    } finally {
      setBusy(false);
    }
  }

  async function handleReject() {
    setError("");
    setBusy(true);
    try {
      await api.recruitment.reject(id, rejectReason);
      setShowRejectForm(false);
      await load();
    } catch (e) {
      setError(e.message || "Could not reject this applicant.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="p-8 text-center text-sm text-gray-400">Loading applicant…</div>;
  if (!applicant) return <div className="p-8 text-center text-sm text-gray-400">Applicant not found.</div>;

  const isDecided = applicant.status !== "New";

  return (
    <div>
      <Link to="/admin/recruitment" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4">
        ← Back to Recruitment
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "#154E93" }}>
            {applicant.first_name} {applicant.last_name}
          </h1>
          <p className="text-sm text-gray-500">{applicant.job_type} · Applied {applicant.created_at?.slice(0, 10)}</p>
        </div>
        <div className="text-right">
          {applicant.status === "Approved" && (
            <div className="text-sm text-green-700 flex items-center gap-1.5 justify-end">
              <span>✓</span> Approved
              {applicant.converted_employee_id && (
                <Link to={`/admin/employees/${applicant.converted_employee_id}`} className="underline ml-1">
                  View employee record
                </Link>
              )}
            </div>
          )}
          {applicant.status === "Rejected" && (
            <div className="text-sm text-red-600 flex items-center gap-1.5 justify-end">
              <span>✕</span> Rejected
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-5">{error}</div>
      )}

      {!isDecided && (
        <div className="bg-white rounded-lg shadow-sm p-5 mb-6 flex items-center justify-between">
          <p className="text-sm text-gray-600">Approving auto-creates the Employee record with zero manual re-entry.</p>
          <div className="flex gap-2">
            <button
              onClick={() => setShowRejectForm((s) => !s)}
              disabled={busy}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-60"
            >
              Reject
            </button>
            <button
              onClick={handleApprove}
              disabled={busy}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white flex items-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: "#154E93" }}
            >
              {busy && <Spinner size={14} />}
              Approve & Create Employee
            </button>
          </div>
        </div>
      )}

      {showRejectForm && !isDecided && (
        <div className="bg-white rounded-lg shadow-sm p-5 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Reason (optional, internal only)</label>
          <textarea
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-3"
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <button
            onClick={handleReject}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-60"
          >
            Confirm Reject
          </button>
        </div>
      )}

      {applicant.status === "Rejected" && applicant.rejection_reason && (
        <Card title="Rejection Reason">
          <p className="text-sm text-gray-700">{applicant.rejection_reason}</p>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card title="General Information">
          <div className="grid grid-cols-2 gap-x-4">
            <Field label="First Name" value={applicant.first_name} />
            <Field label="Last Name" value={applicant.last_name} />
            <Field label="Other Names" value={applicant.other_names} />
            <Field label="Date of Birth" value={applicant.date_of_birth} />
            <Field label="Gender" value={applicant.gender} />
            <Field label="Email" value={applicant.email} />
          </div>
        </Card>

        <Card title="Address">
          <Field label="Street Address" value={applicant.address} />
          <div className="grid grid-cols-3 gap-x-4">
            <Field label="City" value={applicant.city} />
            <Field label="State" value={applicant.state} />
            <Field label="ZIP" value={applicant.zip} />
          </div>
        </Card>

        <Card title="Phone Numbers">
          {applicant.phones?.length ? (
            applicant.phones.map((p) => (
              <Field key={p.id} label={p.phone_type || "Phone"} value={p.phone_number} />
            ))
          ) : (
            <p className="text-sm text-gray-400">None provided.</p>
          )}
        </Card>

        <Card title="Screening Questions">
          <Field label="Work Authorized in the US" value={boolLabel(applicant.work_authorized)} />
          <Field label="18 Years or Older" value={boolLabel(applicant.age_18_plus)} />
          <Field label="Has EVV-Capable Smartphone" value={boolLabel(applicant.has_evv_smartphone)} />
          <Field label="Willing to Undergo Background Check" value={boolLabel(applicant.willing_background_check)} />
          <Field label="Travel Radius (miles)" value={applicant.travel_radius_miles} />
        </Card>

        <Card title="Education">
          {applicant.education?.length ? (
            applicant.education.map((ed) => (
              <div key={ed.id} className="mb-4 pb-4 border-b border-gray-50 last:border-0 last:mb-0 last:pb-0">
                <div className="grid grid-cols-2 gap-x-4">
                  <Field label="School" value={ed.school_name} />
                  <Field label="Degree / Certificate" value={ed.degree} />
                  <Field label="Field of Study" value={ed.field_of_study} />
                  <Field label="Graduation Year" value={ed.graduation_year} />
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-400">None provided.</p>
          )}
        </Card>

        <Card title="References">
          {applicant.references?.length ? (
            applicant.references.map((r) => (
              <ReferenceRow key={r.id} reference={r} onChange={load} />
            ))
          ) : (
            <p className="text-sm text-gray-400">None provided.</p>
          )}
        </Card>

        <Card title="Employment History">
          {applicant.employment_history?.length ? (
            applicant.employment_history.map((eh) => (
              <div key={eh.id} className="mb-4 pb-4 border-b border-gray-50 last:border-0 last:mb-0 last:pb-0">
                <div className="grid grid-cols-2 gap-x-4">
                  <Field label="Employer" value={eh.employer_name} />
                  <Field label="Job Title" value={eh.job_title} />
                  <Field label="Start Date" value={eh.start_date} />
                  <Field label="End Date" value={eh.end_date} />
                  <Field label="Reason for Leaving" value={eh.reason_for_leaving} />
                  <Field label="Supervisor" value={eh.supervisor_name} />
                  <Field label="Supervisor Phone" value={eh.supervisor_phone} />
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-400">None provided.</p>
          )}
        </Card>

        <Card title="Applicant Notes">
          <p className="text-sm text-gray-700">{applicant.applicant_notes || "None provided."}</p>
        </Card>
      </div>
    </div>
  );
}

const REFERENCE_STATUS_COLORS = {
  "Not Contacted": "bg-gray-100 text-gray-500",
  "Contacted": "bg-yellow-50 text-yellow-700",
  "Verified": "bg-green-50 text-green-700",
  "Could Not Reach": "bg-red-50 text-red-700",
};

function ReferenceRow({ reference, onChange }) {
  const [notes, setNotes] = useState(reference.verification_notes || "");

  const updateStatus = async (status) => {
    await api.recruitment.updateReference(reference.id, { verification_status: status });
    onChange();
  };
  const saveNotes = async () => {
    if (notes !== (reference.verification_notes || "")) {
      await api.recruitment.updateReference(reference.id, { verification_notes: notes });
      onChange();
    }
  };

  return (
    <div className="mb-4 pb-4 border-b border-gray-50 last:border-0 last:mb-0 last:pb-0">
      <div className="grid grid-cols-2 gap-x-4 mb-2">
        <Field label="Name" value={reference.reference_name} />
        <Field label="Relationship" value={reference.relationship} />
        <Field label="Phone" value={reference.phone} />
        <Field label="Email" value={reference.email} />
      </div>
      <div className="flex items-center gap-3 mb-2">
        <select
          value={reference.verification_status || "Not Contacted"}
          onChange={(e) => updateStatus(e.target.value)}
          className={`border rounded px-2 py-1 text-xs font-medium ${REFERENCE_STATUS_COLORS[reference.verification_status] || ""}`}
        >
          <option>Not Contacted</option>
          <option>Contacted</option>
          <option>Verified</option>
          <option>Could Not Reach</option>
        </select>
        {reference.verified_by && reference.verified_date && (
          <span className="text-xs text-gray-400">by {reference.verified_by} on {reference.verified_date}</span>
        )}
      </div>
      <input
        className="w-full border rounded px-2 py-1.5 text-xs"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        onBlur={saveNotes}
        placeholder="What did they say? (optional)"
      />
    </div>
  );
}
